// Gallery data structure with all categories and images
// Using Vite's import.meta.glob to dynamically import all images

// Import images for each category using Vite's glob import
// import.meta.glob returns an object with file paths as keys and import functions as values
// With eager: true, it imports immediately and returns the actual modules

const singleGravesModules = import.meta.glob('../static/gallery/single-graves/*.{jpeg,jpg,png}', { eager: true });
const doubleGraveModules = import.meta.glob('../static/gallery/double-grave/*.{jpeg,jpg,png}', { eager: true });
const familyGraveModules = import.meta.glob('../static/gallery/family-grave/*.{jpeg,jpg,png}', { eager: true });
const specialGraveModules = import.meta.glob('../static/gallery/special-grave/*.{jpeg,jpg,png}', { eager: true });
const singleBlackGraniteTombModules = import.meta.glob('../static/gallery/single-black-granite-tomb/*.{jpeg,jpg,png}', { eager: true });
const halfGraveModules = import.meta.glob('../static/gallery/half-grave/*.{jpeg,jpg,png}', { eager: true });
const candleDoorModules = import.meta.glob('../static/gallery/candle-door/*.{jpeg,jpg,png}', { eager: true });
const innerCandleDoorModules = import.meta.glob('../static/gallery/inner-candle-door/*.{jpeg,jpg,png}', { eager: true });
const vaseModules = import.meta.glob('../static/gallery/vase/*.{jpeg,jpg,png}', { eager: true });
const cncEngravingModules = import.meta.glob('../static/gallery/cnc-engraving/*.{jpeg,jpg,png}', { eager: true });
const bronzeLettersModules = import.meta.glob('../static/gallery/bronze-letters/*.{jpeg,jpg,png}', { eager: true });
const leadLettersModules = import.meta.glob('../static/gallery/lead-letters/*.{jpeg,jpg,png}', { eager: true });
const marbleGraniteEngravingModules = import.meta.glob('../static/gallery/marble-granite-engraving/*.{jpeg,jpg,png}', { eager: true });
const boldEngravingModules = import.meta.glob('../static/gallery/bold-engraving/*.{jpeg,jpg,png}', { eager: true });
const sanhadrinMonumentModules = import.meta.glob('../static/gallery/sanhadrin-monument/*.{jpeg,jpg,png}', { eager: true });

const modulesToImages = (modules, categoryId) => {
  return Object.entries(modules).map(([path, module], idx) => ({
    id: `${categoryId}-${idx + 1}`,
    src: module.default || module,
    alt: getImageName(path, categoryId),
    category: categoryId,
  }));
};

const categoryNames = {
  'single-graves': { he: 'קבר בודד', en: 'Single grave' },
  'double-grave': { he: 'קבר כפול', en: 'Double grave' },
  'family-grave': { he: 'קבר משפחה', en: 'Family grave' },
  'special-grave': { he: 'קבר מיוחד', en: 'Special grave' },
  'single-black-granite-tomb': { he: 'מצבת גרניט שחור', en: 'Black granite tombstone' },
  'half-grave': { he: 'קבר חצי', en: 'Half grave' },
  'candle-door': { he: 'בית נר נשמה', en: 'Soul candle house' },
  'inner-candle-door': { he: 'דלת נר נשמה', en: 'Soul candle door' },
  'vase': { he: 'אגרטל', en: 'Vase' },
  'cnc-engraving': { he: 'חיתוך CNC', en: 'CNC engraving' },
  'bronze-letters': { he: 'אותיות ברונזה', en: 'Bronze letters' },
  'lead-letters': { he: 'אותיות עופרת', en: 'Lead letters' },
  'marble-granite-engraving': { he: 'חיתוך שיש וגרניט', en: 'Marble and granite engraving' },
  'bold-engraving': { he: 'חיתוך בולט', en: 'Bold engraving' },
  'sanhadrin-monument': { he: 'אנדרטת סנהדרין', en: 'Sanhedrin monument' }
};

const getImageName = (path, categoryId) => {
  const filename = path.split('/').pop();
  const baseName = filename
    .replace(/\.(jpeg|jpg|png)$/i, '')
    .replace(/-/g, ' ')
    .replace(/_/g, ' ');
  const categoryName = categoryNames[categoryId]?.he || '';
  return categoryName ? `${categoryName} - ${baseName}` : baseName;
};

// Convert modules to image arrays
const singleGraves = modulesToImages(singleGravesModules, 'single-graves');
const doubleGrave = modulesToImages(doubleGraveModules, 'double-grave');
const familyGrave = modulesToImages(familyGraveModules, 'family-grave');
const specialGrave = modulesToImages(specialGraveModules, 'special-grave');
const singleBlackGraniteTomb = modulesToImages(singleBlackGraniteTombModules, 'single-black-granite-tomb');
const halfGrave = modulesToImages(halfGraveModules, 'half-grave');
const candleDoor = modulesToImages(candleDoorModules, 'candle-door');
const innerCandleDoor = modulesToImages(innerCandleDoorModules, 'inner-candle-door');
const vase = modulesToImages(vaseModules, 'vase');
const cncEngraving = modulesToImages(cncEngravingModules, 'cnc-engraving');
const bronzeLetters = modulesToImages(bronzeLettersModules, 'bronze-letters');
const leadLetters = modulesToImages(leadLettersModules, 'lead-letters');
const marbleGraniteEngraving = modulesToImages(marbleGraniteEngravingModules, 'marble-granite-engraving');
const boldEngraving = modulesToImages(boldEngravingModules, 'bold-engraving');
const sanhadrinMonument = modulesToImages(sanhadrinMonumentModules, 'sanhadrin-monument');

// Gallery data structure
export const galleryCategories = [
  {
    id: 'single-graves',
    images: singleGraves,
  },
  {
    id: 'double-grave',
    images: doubleGrave,
  },
  {
    id: 'family-grave',
    images: familyGrave,
  },
  {
    id: 'special-grave',
    images: specialGrave,
  },
  {
    id: 'single-black-granite-tomb',
    images: singleBlackGraniteTomb,
  },
  {
    id: 'half-grave',
    images: halfGrave,
  },
  {
    id: 'candle-door',
    images: candleDoor,
  },
  {
    id: 'inner-candle-door',
    images: innerCandleDoor,
  },
  {
    id: 'vase',
    images: vase,
  },
  {
    id: 'cnc-engraving',
    images: cncEngraving,
  },
  {
    id: 'bronze-letters',
    images: bronzeLetters,
  },
  {
    id: 'lead-letters',
    images: leadLetters,
  },
  {
    id: 'marble-granite-engraving',
    images: marbleGraniteEngraving,
  },
  {
    id: 'bold-engraving',
    images: boldEngraving,
  },
  {
    id: 'sanhadrin-monument',
    images: sanhadrinMonument,
  },
].filter((category) => category.images.length > 0); // Only include categories with images

// Flatten all images for "all" view
export const allImages = galleryCategories.flatMap((category) => category.images);
